import React from 'react';
import { Card, Descriptions, Image, Spin, Button, Modal, message } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { getOrderDetail, restoreOrderPoints } from '@/api/pointsMall';

const orderStatusMap = {
  0: '待发货',
  1: '已发货',
  2: '已完成',
  3: '已取消',
};

const orderTypeMap = {
  1: '积分兑换',
  2: '购买',
};

const PointsMallDetail: React.FC = () => {
  const { orderId = '' } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: response, isLoading } = useQuery({
    queryKey: ['order-detail', orderId],
    queryFn: () => getOrderDetail(orderId),
    enabled: !!orderId,
  });

  const orderDetail = response?.data;

  const handleRestore = async () => {
    if (!orderDetail) return;

    Modal.confirm({
      title: '确认退还积分',
      content: '确定要退还该订单的积分吗？',
      onOk: async () => {
        try {
          const res = await restoreOrderPoints({
            orderId: orderDetail.orderId,
            userId: orderDetail.userId,
          });
          
          if (res.success) {
            message.success('积分退还成功');
            // 刷新详情数据
            queryClient.invalidateQueries(['order-detail', orderId]);
            // 刷新列表数据
            queryClient.invalidateQueries(['points-mall-order-list']);
          } else {
            message.error(res.message || '积分退还失败');
          }
        } catch (error) {
          message.error('操作失败，请重试');
        }
      },
    });
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!orderDetail) {
    return <div>订单不存在</div>;
  }

  const canRestore = orderDetail.orderStatus !== 3; // 非取消状态可以退还积分

  return (
    <Card 
      title="订单详情"
      extra={
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={() => navigate('/points-mall')}>返回列表</Button>
          {canRestore && (
            <Button type="primary" onClick={handleRestore}>
              退还积分
            </Button>
          )}
        </div>
      }
    >
      <Descriptions column={2}>
        <Descriptions.Item label="订单ID">{orderDetail.orderId}</Descriptions.Item>
        <Descriptions.Item label="用户ID">{orderDetail.userId}</Descriptions.Item>
        <Descriptions.Item label="订单类型">
          {orderTypeMap[orderDetail.orderType as keyof typeof orderTypeMap] || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="订单状态">
          {orderStatusMap[orderDetail.orderStatus as keyof typeof orderStatusMap] || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="商品积分">{orderDetail.points}</Descriptions.Item>
        <Descriptions.Item label="订单总积分">{orderDetail.sumPoints}</Descriptions.Item>
        <Descriptions.Item label="商品数量">{orderDetail.productCount}</Descriptions.Item>
        <Descriptions.Item label="下单时间">
          {orderDetail.createTime ? dayjs(orderDetail.createTime).format('YYYY-MM-DD HH:mm:ss') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="支付时间">
          {orderDetail.payTime ? dayjs(orderDetail.payTime).format('YYYY-MM-DD HH:mm:ss') : '-'}
        </Descriptions.Item>
      </Descriptions>

      <Descriptions title="商品信息" column={1} style={{ marginTop: 24 }}>
        <Descriptions.Item label="商品ID">{orderDetail.productInfoId}</Descriptions.Item>
        <Descriptions.Item label="商品标题">{orderDetail.productTitle}</Descriptions.Item>
        <Descriptions.Item label="商品图片">
          <Image
            src={orderDetail.productShowImage}
            alt={orderDetail.productTitle}
            style={{ maxWidth: 200 }}
          />
        </Descriptions.Item>
        {orderDetail.productImage && (
          <Descriptions.Item label="壁纸下载地址">
            <a href={orderDetail.productImage} target="_blank" rel="noopener noreferrer">
              点击下载
            </a>
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};

export default PointsMallDetail;
